import { DeviceAccessor, LogManager } from "@mcleod/core";
import { ClickEvent, ComponentTypes, PanelStyles, Table, TableProps } from "@mcleod/components";
import { AppTableCrudManager } from "./AppTableCrudManager";
import { AppTableHeadingPanel } from "./AppTableHeadingPanel";
import { StickyComponent } from "../sticky/StickyComponent";

const log = LogManager.getLogger("components.table.AppTable");

export class AppTable extends Table {
    private hasFabBodyPadding: boolean = false;
    private standardBodyBottomPadding: string | null = null;

    constructor(props?: Partial<TableProps>) {
        if (props?._designer == null) {
            props = { ...props, padding: 0, paddingLeft: 0, paddingRight: 0, paddingTop: 0, paddingBottom: 0 };
        }
        super(props);
        this._tbody.classList.add(PanelStyles.hideScroll);
    }

    protected createHeadingPanel() {
        this.headingPanel = new AppTableHeadingPanel(this);
    }

    protected createCrudManager(): AppTableCrudManager {
        return new AppTableCrudManager(this);
    }

    protected override get virtualizedIntersectionObserver(): IntersectionObserver {
        return new IntersectionObserver((entries) => this.handleIntersection(entries), { root: null });
    }

    public override get allowExport(): boolean {
        return false;
    }

    public override set allowExport(value: boolean) {
        if (value)
            log.debug("Attempted to allowExport, but app strictly prevents this.");
        super.allowExport = false;
    }

    public override get allowPin(): boolean {
        return false;
    }

    public override set allowPin(value: boolean) {
        if (value)
            log.debug("Attempted to allowPin, but app strictly prevents this.");
        super.allowPin = false;
    }

    public override get allowShare(): boolean {
        return false;
    }

    public override set allowShare(value: boolean) {
        if (value)
            log.debug("Attempted to allowShare, but app strictly prevents this.");
        super.allowShare = false;
    }

    public override get allowConfig(): boolean {
        return false;
    }

    public override set allowConfig(value: boolean) {
        if (value)
            log.debug("Attempted to allowConfig, but app strictly prevents this.");
        super.allowConfig = false;
    }

    public override get allowDetail(): boolean {
        return super.allowDetail;
    }

    public set allowDetail(value: boolean) {
        if (value !== true && this.rowProps != null) {
            delete this.rowProps.onClick;
        }
        this._allowDetail = value;
        if (value === true) {
            if (this.rowProps == null)
                this.rowProps = {};
            this.rowProps.onClick = (event: ClickEvent) => this.crudManager.showDetailFromClick(event);
        }
        this.headingPanel.syncStandardTools();
    }

    public get ignoreRowColorSetting(): boolean {
        return true;
    }

    public set ignoreRowColorSetting(value: boolean) {
        if (!value)
            log.debug("Attempted to allow row color settings, but app strictly prevents this.");
        super.ignoreRowColorSetting = true;
    }

    protected override insertQuickAddRowInternal(updateTableContent: boolean): void {
        // Do nothing. AppTables should not show quick add rows. We do, however, want the quick add row to
        // exist and be set up, so we can use the edit layout to add a new row.
    }

    protected override getSerializedColumnDefinitions(compDef: any): any {
        if (compDef.appProps?.columns != null && !this.usesStandardColumnSet(compDef)) {
            return compDef.appProps.columns;
        }
        return super.getSerializedColumnDefinitions(compDef);
    }

    private usesStandardColumnSet(compDef: any): boolean {
        return DeviceAccessor.isTablet && compDef.appProps?.tabletUsesStandardColumns === true;
    }

    // Adds padding to ensure we don't cover final row in table.
    applyFabBodyPadding() {
        this.standardBodyBottomPadding ??= this._tbody.style.paddingBottom ?? "0px";

        if (!this.hasFabBodyPadding) {
            this._tbody.style.paddingBottom = "120px";
            this.hasFabBodyPadding = true;
        }
    }

    removeFabBodyPadding() {
        this.standardBodyBottomPadding ??= this._tbody.style.paddingBottom ?? "0px";

        if (this.hasFabBodyPadding) {
            this._tbody.style.paddingBottom = this.standardBodyBottomPadding;
            this.hasFabBodyPadding = false;
        }
    }

    // stickyComponent getter property defined for Component class by AppComponent#initialize.
    // Used in AppStickyManager.
    get stickyComponents(): StickyComponent[] {
        return [
            { element: this.headingPanel._element, contentComponent: this },
            { element: this._thead, contentComponent: this }
        ];
    }
}

ComponentTypes.registerAppComponentType("table", {
    constructor: AppTable.prototype.constructor
});
